// Design tokens for the POS redesign (New + Retrieve screens).
// One source of truth — components must not hardcode these values.

export const COLORS = {
  ink: "#3a3a7a", // primary indigo (text, accents, CTA)
  inkSoft: "#6b6b85", // secondary text that still passes 4.5:1 at small sizes
  muted: "#8a8aa5", // tertiary text — 14px+ only
  cream: "#ffffff", // page background (white — cards distinguish via border+shadow)
  card: "#ffffff",
  line: "#e7e7f0",
  lavender: "#f0f0fa", // selected / soft fills
  teal: "#2bb3a3", // OUT direction accent only
  tealDark: "#1d8389", // pressed/active on teal surfaces
  green: "#1f9d57",
  amber: "#b87514",
};

export const RADIUS = {
  card: 16,
  row: 12,
  pill: 999,
};

export const SECTION_EYEBROW = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: COLORS.muted,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

// THE elevation for cards/tiles — one shadow everywhere, no ad-hoc values.
export const CARD_SHADOW = "0 2px 10px rgba(58, 58, 122, 0.10)";
// Hero container (e.g. the framed success pages) — one step above CARD_SHADOW.
export const HERO_SHADOW = "0 10px 32px rgba(58, 58, 122, 0.14)";

export const FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

// ─────────────────────────────────────────────────────────────────────────
// WOSH Refresh design system (2026 relooking) — mirrors the --wosh-* tokens
// from the Claude Design export. Namespaced as `WR` so it coexists with the
// legacy tokens above: screens migrate to WR one at a time as they're
// re-skinned (nothing changes until a screen opts in). VISUAL ONLY.
// ─────────────────────────────────────────────────────────────────────────
export const WR = {
  color: {
    brand: "#3D4586",
    brandDark: "#242B63",
    lavande: "#7480E0",
    lavSoft: "#E7E9FF",
    lavSel: "#ECEEFF",
    menthe: "#8AE3E8",
    mintDeep: "#34A6AC",
    mintBg: "rgba(52, 166, 172, 0.12)",
    accentSoft: "#E7F8FB",
    encre: "#1B1F3A",
    inkMuted: "#6B6F88",
    inkSoft: "#9499B8",
    text: "#33406B",
    textMuted: "#6B7392",
    bg: "#F0F1F8",
    bgCheckout: "#F8F9FA",
    surface: "#FFFFFF",
    surfaceSoft: "#E7E9FF",
    cardMuted: "#F1F2FB",
    border: "#D8DDF0",
    borderLight: "#D8DCEE",
    hairline: "rgba(27, 31, 58, 0.07)",
    success: "#1A9C68",
    warning: "#E7A93B",
    danger: "#D84F68",
    error: "#B91C1C",
    errorBg: "#FEE2E2",
    errorBorder: "#FECACA",
    // semantic aliases
    textHeading: "#1B1F3A",
    textBody: "#1B1F3A",
    textMeta: "#6B6F88",
    textOnBrand: "#FFFFFF",
    surfaceCard: "#FFFFFF",
    surfaceApp: "#F0F1F8",
    accent: "#34A6AC",
  },
  font: {
    sans: "'Manrope', -apple-system, system-ui, 'Segoe UI', sans-serif",
    mono: "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace",
  },
  weight: { regular: 400, medium: 500, semibold: 600, bold: 700, extra: 800 },
  // font sizes (px)
  text: {
    display: 32,
    hero: 30,
    h1: 28,
    titleLg: 26,
    h2: 22,
    h3: 18,
    subhead: 17,
    bodyLg: 16,
    slot: 15,
    body: 14,
    small: 13,
    caption: 12,
    eyebrow: 11,
  },
  ls: { tight: -0.5, snug: -0.2, normal: 0, eyebrow: 1.6, label: 1.4, logo: 2 },
  lh: { tight: 1.1, snug: 1.3, body: 1.5 },
  space: { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32, xxxl: 48 },
  radius: {
    sm: 12,
    md: 14,
    card: 18,
    lg: 24,
    xl: 26,
    footer: 28,
    phone: 44,
    pill: 999,
  },
  shadow: {
    card: "0 14px 24px -14px rgba(61, 69, 134, 0.18), 0 2px 6px -2px rgba(61, 69, 134, 0.05)",
    cta: "0 8px 16px -6px rgba(61, 69, 134, 0.28)",
    float: "0 18px 50px -18px rgba(61, 69, 134, 0.32)",
    control: "0 4px 18px -8px rgba(61, 69, 134, 0.18)",
  },
};
